import { useAuth } from '@clerk/clerk-react';
import { toast } from '@startupquickstart/frontend';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { getAiJobSocket, type AiJobScope, type AiJobState } from '@/lib/aiJobSocket';
import { setAiJobHandoffHandler } from './aiJobHandoff';

const STORAGE_KEY = 'aiJobs:handedOff';

/**
 * Returns the localStorage key namespaced to the current user.
 * Different users on the same browser shouldn't see each other's job state.
 */
function buildStorageKey(userId: string | null | undefined): string {
  return `${STORAGE_KEY}:${userId ?? 'anon'}`;
}

function readHandedOffJobs(userId: string | null | undefined): string[] {
  try {
    const raw = window.localStorage.getItem(buildStorageKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v) => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

function writeHandedOffJobs(userId: string | null | undefined, jobIds: string[]): void {
  try {
    if (jobIds.length === 0) {
      window.localStorage.removeItem(buildStorageKey(userId));
    } else {
      window.localStorage.setItem(buildStorageKey(userId), JSON.stringify(jobIds));
    }
  } catch {
    // Storage may be disabled (private mode, quota); ignore.
  }
}

/**
 * Build a one-line description for the completion toast based on the scope.
 */
function describeScope(scope: AiJobScope): string {
  const ACTION_LABELS: Record<string, string> = {
    'generate-descriptions': 'Descriptions',
    'generate-voiceovers': 'Voiceovers',
    'generate-translations': 'Translations',
    'generate-translation-voiceovers': 'Translation voiceovers',
    'generate-content': 'Tour details',
    'generate-conclusion': 'Conclusion',
  };
  const ENTITY_LABELS: Record<string, string> = {
    attraction: 'attraction',
    'attraction-theme': 'theme',
    tour: 'tour',
    'attraction-translation': 'attraction translation',
    'attraction-theme-translation': 'theme translation',
    'tour-translation': 'tour translation',
  };
  const action = ACTION_LABELS[scope.action] ?? scope.action;
  const entity = ENTITY_LABELS[scope.entityType] ?? scope.entityType;
  return `${action} ready for ${entity}`;
}

/**
 * Invalidate the React Query keys most likely to need refreshing when a job
 * completes for the given scope. Best-effort: any query keyed by the entity
 * id will refetch on next mount even without explicit invalidation, so this
 * mostly speeds up pages the user is currently looking at.
 */
function invalidateForScope(
  queryClient: ReturnType<typeof useQueryClient>,
  scope: AiJobScope,
): void {
  switch (scope.entityType) {
    case 'attraction':
      queryClient.invalidateQueries({ queryKey: ['Attraction', scope.entityId] });
      break;
    case 'attraction-theme':
      queryClient.invalidateQueries({
        queryKey: ['AttractionTheme', scope.parentEntityId, scope.entityId],
      });
      break;
    case 'tour':
      queryClient.invalidateQueries({ queryKey: ['Tour', scope.entityId] });
      break;
    case 'attraction-translation':
    case 'attraction-theme-translation':
    case 'tour-translation':
      // The detail pages key by translationId; invalidate broadly.
      queryClient.invalidateQueries({ queryKey: ['Translation'] });
      break;
  }
}

/**
 * Mount once at the app root. Listens to the singleton AI-job socket on
 * behalf of the whole app and fires a toast whenever a backgrounded job
 * finishes — including jobs that were started before a page reload, since
 * the active job ids are persisted in localStorage.
 */
export function AiJobNotificationProvider({ children }: { children: React.ReactNode }) {
  const { getToken, userId } = useAuth();
  const queryClient = useQueryClient();
  // Keep a live ref to the user's active job ids so the socket-event listener
  // can decide quickly whether a state event belongs to this user.
  const activeJobsRef = useRef<Set<string>>(new Set());

  // Hydrate from localStorage and from incoming handoff calls.
  const persistActive = useCallback(() => {
    writeHandedOffJobs(userId, [...activeJobsRef.current]);
  }, [userId]);

  useEffect(() => {
    activeJobsRef.current = new Set(readHandedOffJobs(userId));
  }, [userId]);

  useEffect(() => {
    setAiJobHandoffHandler((jobId: string) => {
      activeJobsRef.current.add(jobId);
      persistActive();
    });
    return () => {
      setAiJobHandoffHandler(null);
    };
  }, [persistActive]);

  useEffect(() => {
    let cancelled = false;

    const onState = (next: AiJobState) => {
      if (cancelled) return;
      if (!activeJobsRef.current.has(next.jobId)) return;

      if (next.status === 'completed') {
        toast.success(`${describeScope(next.scope)} — background generation finished.`);
        invalidateForScope(queryClient, next.scope);
        activeJobsRef.current.delete(next.jobId);
        persistActive();
      } else if (next.status === 'failed') {
        const reason = next.error ?? 'Background generation failed.';
        toast.error(`${describeScope(next.scope)} — failed: ${reason}`);
        activeJobsRef.current.delete(next.jobId);
        persistActive();
      }
    };

    void (async () => {
      const socket = await getAiJobSocket(getToken);
      if (cancelled) return;

      // Re-subscribe to any jobs the user had handed off before this mount
      // (for example after a page reload). Best-effort — expired jobs return
      // an error from the server and we silently drop them.
      for (const jobId of activeJobsRef.current) {
        socket.emit('ai-job:join', { jobId }, (response: { state?: AiJobState; error?: string }) => {
          if (cancelled) return;
          if (response?.error) {
            // Job has expired (TTL) or never existed; drop it locally.
            activeJobsRef.current.delete(jobId);
            persistActive();
            return;
          }
          if (response?.state) onState(response.state);
        });
      }

      socket.on('ai-job:state', onState);
    })();

    return () => {
      cancelled = true;
      void getAiJobSocket(getToken).then((socket) => {
        socket.off('ai-job:state', onState);
      });
    };
  }, [getToken, persistActive, queryClient]);

  return <>{children}</>;
}
